import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import Values from "values.js";
import timeZoneToCountry from "../../../constants/timeZoneToCountry";
import { CurrencyData } from "../../../constants/countryData";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);

const daysLocale = dayjs().localeData();

export const isEmpty = (value) => {
  if (value === 0 || value === undefined || value === null) return true;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return true; // Invalid Date
    return false; // Valid Date
  }
  if (typeof value === "object" && Object.keys(value).length === 0) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
};


export const upiIntentLogoDictionary = (id) => {
  const dict = {
    PhonePe: "/assets/Icons/UPI Icons/PhonePe.svg",
    GooglePay: "/assets/Icons/UPI Icons/GooglePay.svg",
    Paytm: "/assets/Icons/UPI Icons/Paytm.svg",
  };
  return id in dict ? dict[id] : null;
};
export const getUTCDayRange = (selectedDropdownValue) => {

  let dayOffset;
  const fromDate = new Date();
  const toDate = new Date();

  switch (selectedDropdownValue) {
    case "yesterday":
      dayOffset = 1;
      toDate.setDate(toDate.getDate() - 1);
      break;
    case "today":
      dayOffset = 0;
      break;
    case "thisWeek":
      const currentDay = fromDate.getDay();
      const mondayOffset = (currentDay + 6) % 7;
      const sundayOffset = 6 - currentDay;

      fromDate.setDate(fromDate.getDate() - mondayOffset);
      toDate.setDate(toDate.getDate() + sundayOffset);
      dayOffset = 0;
      break;
    case "lastWeek":
      dayOffset = 7;
      toDate.setDate(toDate.getDate() - 1);
      break;
    case "thisLastMonth":
      fromDate.setDate(1);
      toDate.setMonth(toDate.getMonth() + 1, 0);
      dayOffset = 0;
      break;
    case "thisMonth":
      dayOffset = 30;
      toDate.setDate(toDate.getDate() - 1);
      break;
    case "thisYear":
      dayOffset = 0;
      fromDate.setUTCMonth(0, 1);
      break;

    case "last3Months":
      fromDate.setMonth(fromDate.getMonth() - 3, 1); // Start of 3 months ago
      fromDate.setHours(0, 0, 0, 0);
      break;

    case "last6Months":
      fromDate.setMonth(fromDate.getMonth() - 6, 1); // Start of 6 months ago
      fromDate.setHours(0, 0, 0, 0);
      break;

    case "last12Months":
      fromDate.setMonth(fromDate.getMonth() - 12, 1); // Start of 12 months ago
      fromDate.setHours(0, 0, 0, 0);
      break;
    default:
      dayOffset = 0;
      break;
  }
  if (
    selectedDropdownValue !== "thisWeek" &&
    selectedDropdownValue !== "thisLastMonth" &&
    selectedDropdownValue !== 'last3Months' &&
    selectedDropdownValue !== 'last6Months' &&
    selectedDropdownValue !== 'last12Months'
  ) {
    fromDate.setDate(fromDate.getDate() - dayOffset);
  }
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);

  return { fromDate, toDate };
};

export const getCustomDateRange = (singleStartDate, startDate, endDate) => {
  if (singleStartDate) {
    const fromDate = new Date(singleStartDate);
    fromDate.setUTCHours(18, 30, 0, 0);

    const toDate = new Date(singleStartDate);
    toDate.setDate(toDate.getDate() + 1);
    toDate.setUTCHours(18, 29, 59, 999);

    return { fromDate: fromDate.toISOString(), toDate: toDate.toISOString() };
  } else if (startDate && endDate) {
    const fromDate = new Date(startDate);
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date(endDate);
    toDate.setHours(23, 59, 59, 999);
    return { fromDate: fromDate.toISOString(), toDate: toDate.toISOString() };
  }
  return null;
};

export const isTodayDate = (date) => {
  const today = dayjs().startOf("day");
  const checkDate = dayjs(date).startOf("day");
  return today.isSame(checkDate);
};

export const getUTCDayRangePercentage = (selectedDropdownValue) => {
  let fromDate = new Date();
  const toDate = new Date();

  switch (selectedDropdownValue) {
    case "yesterday":
      fromDate.setDate(fromDate.getDate() - 2);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setDate(toDate.getDate() - 2);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "today":
      fromDate.setDate(fromDate.getDate() - 1);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setDate(toDate.getDate() - 1);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "thisWeek":
      {
        const currentDay = fromDate.getDay();
        const mondayOffset = ((currentDay + 6) % 7) + 7; // Last week's Monday
        const sundayOffset = currentDay + 1; // Last week's Sunday

        fromDate.setDate(fromDate.getDate() - mondayOffset);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setDate(toDate.getDate() - sundayOffset);
        toDate.setHours(23, 59, 59, 999);
      }
      break;
    case "lastWeek":
      fromDate.setDate(fromDate.getDate() - 7 - 7);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setDate(toDate.getDate() - 7 - 1);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "thisLastMonth":
      fromDate.setMonth(fromDate.getMonth() - 1, 1);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setMonth(toDate.getMonth(), 0);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      fromDate.setDate(fromDate.getDate() - 30 - 30);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setDate(toDate.getDate() - 30 - 1);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "thisYear":
      fromDate.setFullYear(fromDate.getFullYear());
      fromDate.setMonth(0, 1);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setFullYear(toDate.getFullYear());
      toDate.setMonth(11, 31);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "last3Months":
      fromDate.setMonth(fromDate.getMonth() - 6, 1); // Start of 6 months ago
      fromDate.setHours(0, 0, 0, 0);

      toDate.setMonth(toDate.getMonth() - 3, 0); // End of 3 months ago
      toDate.setHours(23, 59, 59, 999);
      break;
    case "last6Months":
      fromDate.setMonth(fromDate.getMonth() - 12, 1); // Start of 12 months ago
      fromDate.setHours(0, 0, 0, 0);

      toDate.setMonth(toDate.getMonth() - 6, 0); // End of 6 months ago
      toDate.setHours(23, 59, 59, 999);
      break;
    case "last12Months":
      fromDate.setFullYear(fromDate.getFullYear() - 1);
      fromDate.setMonth(fromDate.getMonth()); // Keep the same day but one year back
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999); // Keep today as the end date
      break;

    default:
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      break;
  }

  return { fromDate, toDate };
};
export function camelCaseToNormal(text) {
  return text
    .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
}

export const getCalendarInterval = (dropdownValue) => {
  switch (dropdownValue) {
    case "yesterday":
      return "1w";
    case "today":
      return "1w";
    case "thisWeek":
      return "1y";
    case "lastWeek":
      return "1y";
    case "thisLastMonth":
      return "1y";
    case "thisMonth":
      return "1y";
    case "last3Months":
      return "1y";
    case "last3Months":
      return "1y";
    case "last6Months":
      return "1y";
    case "last12Months":
      return "1y";
    case "thisYear":
      return "1y";
    default:
      return "1y";
  }
};

export const getCalendarIntervalForGraph = (dropdownValue) => {
  switch (dropdownValue) {
    case "yesterday":
      return "1h";
    case "today":
      return "1h";
    case "thisWeek":
      return "1d";
    case "lastWeek":
      return "1d";
    case "thisLastMonth":
      return "1d";
    case "thisMonth":
      return "1d";
    case "thisYear":
      return "1M";
    case "last3Months":
      return "1M";
    case "last6Months":
      return "1M";
    case "last12Months":
      return "1M";
    default:
      return "1h";
  }
};

export const isPastTime = (timeLabel, currentHour, currentMinute) => {
  const [labelHour, labelMinute] = timeLabel.split(":");
  const labelTime = {
    hour: parseInt(labelHour),
    minute: parseInt(labelMinute),
  };

  return (
    currentHour > labelTime.hour ||
    (currentHour === labelTime.hour && currentMinute >= labelTime.minute)
  );
};

export const pushDataByTimeLabel = (
  labels,
  dataByPSP,
  filterGmvData,
  selectedOptions,
  timeLabel,
  currentHour,
  currentMinute,
  isToday,
  isLastLabel,
  successRate
) => {
  selectedOptions.forEach((pspCodeLocale) => {
    const isPastTime = isToday
      ? currentHour > parseInt(timeLabel.split(":")[0]) ||
      (currentHour === parseInt(timeLabel.split(":")[0]) &&
        currentMinute >= parseInt(timeLabel.split(":")[1]))
      : true;

    if (isPastTime || isLastLabel) {
      const dataItem = filterGmvData?.find((item) => {
        const timestampParts = item?.timestamp?.split(" ");
        const timePart = timestampParts[1];
        const [hours, minutes] = timePart.split(":");
        const extractedTime = `${hours}:${minutes}`;

        return (
          (isLastLabel && extractedTime === timeLabel) ||
          (!isLastLabel &&
            extractedTime?.includes(timeLabel) &&
            item?.pspCodeLocale === pspCodeLocale)
        );
      });
      if (dataItem) {
        if (!dataByPSP[pspCodeLocale]) {
          dataByPSP[pspCodeLocale] = [];
        }
        dataByPSP[pspCodeLocale].push(dataItem[successRate]);
      } else {
        if (!dataByPSP[pspCodeLocale]) {
          dataByPSP[pspCodeLocale] = [];
        }
        dataByPSP[pspCodeLocale].push(0);
      }
    } else {
      if (!dataByPSP[pspCodeLocale]) {
        dataByPSP[pspCodeLocale] = [];
      }
      dataByPSP[pspCodeLocale].push(null);
    }
  });
};

const timeZoneMap = {
  IST: "Asia/Kolkata",
  PST: "America/Los_Angeles",
  EST: "America/New_York",
  // Add more mappings as needed
};
export function defaultListDate(reduceMonth = 13) {
  let toDate, fromDate
  toDate = new Date();
  toDate.setHours(23, 59, 59, 999);
  fromDate = new Date(toDate);
  fromDate.setMonth(fromDate.getMonth() - reduceMonth);
  fromDate.setHours(0, 0, 0, 0);
  return { toDate, fromDate }
}
export function extractTextFromHTML(htmlString) {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent || tempDiv.innerText || "";
}
export const convertIsoToGivenTimeZone = (date, timeZone) => {
  const validTimeZone = timeZoneMap[timeZone] || timeZone; // Fallback to original if not in map
  try {
    const time = dayjs(date).tz(validTimeZone).format("DD/MM/YYYY HH:mm:ss");
    return time + " " + validTimeZone;
  } catch (error) {
    return null; // Handle the error appropriately
  }
};


export const convertIsoToGivenDateFormat = (date, timeZone = "IST") => {
  const validTimeZone = timeZoneMap[timeZone] || timeZone;
  try {
    return dayjs.utc(date).tz(validTimeZone).format("DD/MM/YYYY HH:mm:ss") + ` ${timeZone}`;
  } catch (error) {
    console.error("Date conversion error:", error);
    return null;
  }
};
export const convertIsoToGivenMonthYear = (date, timeZone = "IST") => {
  const validTimeZone = timeZoneMap[timeZone] || timeZone;
  try {
    return dayjs.utc(date).tz(validTimeZone).format("DD/MM/YYYY");
  } catch (error) {
    console.error("Date conversion error:", error);
    return null;
  }
};

export const isSameDate = (date1, date2, format = "DD/MM/YYYY") => {
  if (!dayjs(date1).isValid() || !dayjs(date2).isValid()) {
    console.error("Invalid date input");
    return false;
  }

  return dayjs(date1).format(format) === dayjs(date2).format(format);
};

export const parseAsDate = (dateString) => {
  if (dateString) {
    const timezone = dateString.substring(dateString.lastIndexOf(" ") + 1);
    const ianaTimezone = timeZoneMap[timezone] || timezone; // Fallback to original if not in map
    try {
      return dayjs.tz(dateString, "DD/MM/YYYY HH:mm:ss", ianaTimezone);
    } catch (error) {
      return null; // or handle the error appropriately
    }
  }
};

export const currentTimeInTimezone = (date, timezone) => {
  return dayjs(date).tz(timezone).format("YYYY-MM-DDTHH:mm:ss[Z]");
};

// export const fortmatTimezoneToUT
export function generateDateTimezone(key, dateStr) {


  if (key === 'end' && !dateStr) {
    return dayjs().toISOString(); // Return today's date in ISO format
  }


  const [year, month] = dateStr.split('-');

  if (key === 'start') {
    return dayjs(`${year}-${month}-01T18:30:00.000Z`).toISOString();
  } else if (key === 'end') {
    const lastDay = dayjs(`${year}-${month}-01`).endOf('month').date();
    return dayjs(`${year}-${month}-${lastDay}T18:30:00.000Z`).toISOString();
  }
}

export function generateDateValueOf(key, dateStr) {
  if (key === "end" && !dateStr) {
    return dayjs().valueOf();
  }


  // // Ensure dateStr is valid
  // if (!dateStr || !/^\d{4}-\d{2}$/.test(dateStr)) {
  //   throw new Error("Invalid dateStr format. Expected format: 'YYYY-MM'");
  // }

  const [year, month] = dateStr.split("-");

  if (key === "start") {
    return dayjs(`${year}-${month}-01T00:00:00.000Z`).valueOf();
  } else if (key === "end") {
    const lastDay = dayjs(`${year}-${month}-01`).endOf("month").format("DD");
    return dayjs(`${year}-${month}-${lastDay}T23:59:59.999Z`).valueOf();
  }

  // throw new Error("Invalid key. Expected 'start' or 'end'.");
}

export const fortmatTimezoneToUTC = (value, startDate, endDate) => {
  const formatDateToUTC = (date) => {
    return dayjs(date).tz("UTC").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  };

  let fromDate, toDate;
  let selectedDate = startDate;
  let selectedEndDate = endDate;
  if (value === "last24Months") {
    const startDate1 = new Date();
    startDate1.setMonth(startDate1.getMonth() - 24);
    startDate1.setHours(0, 0, 0, 0);

    const endDate1 = new Date();
    endDate1.setHours(23, 59, 59, 999);

    fromDate = formatDateToUTC(startDate1);
    toDate = formatDateToUTC(endDate1);
  } else if (value === "Last24hours") {
    const startDate = new Date(selectedDate);
    startDate.setHours(startDate.getHours());
    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(selectedEndDate);
  } else if (value === "last15minutes") {
    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedEndDate);
    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(endDate);
  } else if (value === "last7Days") {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setDate(todayEnd.getDate() - 1);
    todayEnd.setHours(23, 59, 59, 999);

    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(todayEnd);
  } else if (value === "lastMonth") {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setDate(todayEnd.getDate() - 1);
    todayEnd.setHours(23, 59, 59, 999);

    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(todayEnd);
  } else if (value === "singleDate") {
    if (selectedDate) {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate());
      const todayEnd = new Date(selectedDate);
      todayEnd.setHours(23, 59, 59, 999);
      todayEnd.setDate(todayEnd.getDate());

      fromDate = formatDateToUTC(startDate);
      toDate = formatDateToUTC(todayEnd);
    }
  } else if (value === "dateRange") {
    if (startDate && endDate) {
      const startDateRange = new Date(startDate);
      startDateRange.setHours(0, 0, 0, 0);
      const todayEnd = new Date(endDate);
      todayEnd.setHours(23, 59, 59, 999);
      todayEnd.setDate(todayEnd.getDate());

      fromDate = formatDateToUTC(startDateRange);
      toDate = formatDateToUTC(todayEnd);
    }
  } else if (value === "customMonth") {
    if (startDate) {
      const startDateObj = new Date(startDate);

      // Set start date to the 1st of the selected month
      const startDateRange = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1);
      startDateRange.setHours(0, 0, 0, 0);

      // Set end date to the last day of the selected month
      const endDateRange = new Date(startDateObj.getFullYear(), startDateObj.getMonth() + 1, 0);
      endDateRange.setHours(23, 59, 59, 999);

      fromDate = formatDateToUTC(startDateRange);
      toDate = formatDateToUTC(endDateRange);
    }
  } else if (value === "last3Months" || value === "last6Months") {
    const monthsOffset = value === "last3Months" ? 3 : 6;
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - monthsOffset); // Go back by the offset
    startDate.setDate(1); // Set to the 1st of the month
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setDate(0); // Set to the last day of the previous month
    endDate.setHours(23, 59, 59, 999);
    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(endDate);
  } else if (value === "lastMonthRecon") {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // Get the current month (0-based index)
    const currentYear = currentDate.getFullYear();
    const startDate = new Date(currentYear, currentMonth - 1, 1); // Previous month, 1st day
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(currentYear, currentMonth, 0); // Last day of the previous month
    endDate.setHours(23, 59, 59, 999);
    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(endDate);
  } else if (value === "thisMonth") {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // Get the current month (0-based index)
    const currentYear = currentDate.getFullYear();
    const startDate = new Date(currentYear, currentMonth, 1); // Current month, 1st day
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(currentYear, currentMonth + 1, 0); // Last day of the current month
    endDate.setHours(23, 59, 59, 999);
    fromDate = formatDateToUTC(startDate);
    toDate = formatDateToUTC(endDate);
  }
  return { fromDate, toDate };
};

export const timeBasedLabel = (dateString) => {
  const daysjs = parseAsDate(dateString);
  const formattedTime = daysjs.format("HH:mm");
  return formattedTime;
};



export const compareDatesForSorting = (date1, date2, userTimeZone) => {
  // Format the dates to the specified time zone
  const formattedDate1 = dayjs(date1).utc();
  const formattedDate2 = dayjs(date2).utc();
  // Compare the two dates
  return formattedDate1.diff(formattedDate2); // Returns the difference in milliseconds
};
export const sortDateTime = (dateString1, dateString2) => {
  const daysjs1 = parseAsDate(dateString1);
  const daysjs2 = parseAsDate(dateString2);

  if (!daysjs1 && !daysjs2) return 0;
  if (!daysjs1) return -1;
  if (!daysjs2) return 1;

  // Compare timestamps
  return daysjs1.valueOf() - daysjs2.valueOf();
};

export const sortPaymentCategory = (
  categoryDataForSorting1,
  categoryDataForSorting2
) => {
  if (categoryDataForSorting1.type === categoryDataForSorting2.type) {
    return categoryDataForSorting1.subType.localeCompare(
      categoryDataForSorting2.subType
    );
  }

  return categoryDataForSorting1.type.localeCompare(
    categoryDataForSorting2.type
  );
};

export const timeLabelForCharts = () => {
  const labels = [];
  const startTime = dayjs().startOf("day");
  const endTime = dayjs().startOf("day").add(1, "day").subtract(1, "minute");
  let currentTime = startTime;
  while (currentTime <= endTime) {
    labels.push(currentTime.format("HH:mm"));
    currentTime = currentTime.add(1, "hour");
  }
  // labels.push("23:59");

  return labels;
};

export const dayMonthBasedLabel = (dateString) => {
  const daysjs = parseAsDate(dateString);
  const day = daysjs.date().toString().padStart(2, "0");
  return `${day} ${daysLocale.monthsShort()[daysjs.month()]}`;
};

export const getLabelsForCurrentWeek = () => {
  const labels = [];
  const today = dayjs();
  const startOfWeek = today.startOf("week").add(1, "day"); // Start of week is Monday
  const endOfWeek = startOfWeek.add(6, "days");
  let currentDay = startOfWeek;
  while (
    currentDay.isBefore(endOfWeek) ||
    currentDay.isSame(endOfWeek, "day")
  ) {
    const timeLabel = currentDay.format("DD MMM");
    labels.push(timeLabel);
    currentDay = currentDay.add(1, "day");
  }
  return labels;
};

export const getLabelsForDateRange = (start, end) => {
  const labels = [];
  let current = start.clone();

  while (current.isBefore(end) || current.isSame(end, "day")) {
    labels.push(current.format("DD MMM"));
    current = current.add(1, "day");
  }
  return labels;
};

export const getLabelsForLast7Days = () => {
  const labels = [];
  let currentTime = dayjs().subtract(1, "day");
  for (let i = 0; i < 7; i++) {
    const timeLabel = currentTime.format("DD MMM");
    labels.unshift(timeLabel);
    currentTime = currentTime.subtract(1, "day");
  }
  return labels;
};

export const getLabelsForCurrentMonth = () => {
  const labels = [];
  const today = dayjs();
  const startOfMonth = today.startOf("month");
  const endOfMonth = today.endOf("month");
  let currentDay = startOfMonth;
  while (
    currentDay.isBefore(endOfMonth) ||
    currentDay.isSame(endOfMonth, "day")
  ) {
    const timeLabel = currentDay.format("DD MMM");
    labels.push(timeLabel);
    currentDay = currentDay.add(1, "day");
  }
  return labels;
};

export const getLabelsForLast30Day = () => {
  const labels = [];
  let currentTime = dayjs().subtract(1, "day");
  for (let i = 0; i < 30; i++) {
    const timeLabel = currentTime.format("DD MMM");
    labels.unshift(timeLabel);
    currentTime = currentTime.subtract(1, "day");
  }
  return labels;
};

export const monthBasedLabel = (dateString) => {
  const daysjs = parseAsDate(dateString);
  return `${daysLocale.monthsShort()[daysjs.month()]}`;
};

export const monthBasedLabelWithYear = (dateString) => {
  const daysjs = parseAsDate(dateString);
  return `${daysLocale.monthsShort()[daysjs.month()]} ${daysjs.year()}`;
};

export const getLabelsForYear = () => {
  const labels = [];
  for (let i = 0; i <= 11; i++) {
    const monthName = dayjs().month(i).format("MMM");
    labels.push(monthName);
  }
  return labels;
};

export const getLabelsForLast3Months = () => {
  const labels = [];
  let currentMonth = dayjs(); // Start from the current month

  for (let i = 0; i < 3; i++) {
    const monthLabel = currentMonth.format("MMM YYYY");
    labels.unshift(monthLabel);
    currentMonth = currentMonth.subtract(1, "month");
  }

  return labels;
};

export const getLabelsForLast6Months = () => {
  const labels = [];
  let currentMonth = dayjs();
  for (let i = 0; i < 6; i++) {
    const monthLabel = currentMonth.format("MMM YYYY");
    labels.unshift(monthLabel);
    currentMonth = currentMonth.subtract(1, "month");
  }
  return labels;
};

export const getLabelsForLast12Months = () => {
  const labels = [];
  let currentMonth = dayjs();
  for (let i = 0; i < 12; i++) {
    const monthLabel = currentMonth.format("MMM YYYY");
    labels.unshift(monthLabel);
    currentMonth = currentMonth.subtract(1, "month");
  }
  return labels;
};
export function formatDateWithoutTimezone(dateString, timezone) {
  if (timezone && dateString?.toLowerCase().includes(timezone.toLowerCase())) {
    const date = dayjs.utc(dateString, `DD/MM/YYYY HH:mm:ss [${timezone}]`);
    return date.isValid() ? date.format("D MMM YYYY, h:mm A") : "";
  }
  const date = dayjs(dateString);
  return date.isValid() ? date.format("D MMM YYYY, h:mm A") : "";
}


export const handleSingleChange =
  (
    selectedOptions,
    setSelectAll,
    setSelectedOptions,
    uniqueOptionTypes,
    selectAll
  ) =>
    (event) => {
      const newSelectedOptions = event.target.value;
      if (newSelectedOptions.includes("Select All")) {
        if (selectAll) {
          setSelectedOptions([]);
          setSelectAll(false);
        } else {
          setSelectedOptions(uniqueOptionTypes);
          setSelectAll(true);
        }
      } else {
        setSelectedOptions((prevSelectedOptions) => {
          const updatedOptions = newSelectedOptions.filter(
            (value) => value !== "Select All"
          );
          const allOtherOptionsSelected =
            updatedOptions.length === uniqueOptionTypes.length &&
            !updatedOptions.includes("Select All");
          setSelectAll(allOtherOptionsSelected);
          return updatedOptions;
        });
      }
    };

export function getPaymentMethodCategories(
  paymentMethodIds,
  paymentMethodIdsInfo
) {
  var newPaymentMethodIdsInfo = [];
  var categories = {};

  for (let index = 0; index < paymentMethodIds?.length; index++) {
    newPaymentMethodIdsInfo.push(
      paymentMethodIdsInfo?.filter((obj) => obj.id === paymentMethodIds[index])
    );
  }

  for (let index = 0; index < newPaymentMethodIdsInfo.length; index++) {
    if (categories[newPaymentMethodIdsInfo[index][0]?.type]) {
      categories[newPaymentMethodIdsInfo[index][0]?.type].push(
        newPaymentMethodIdsInfo[index][0]
      );
    } else {
      categories[newPaymentMethodIdsInfo[index][0]?.type] = [
        newPaymentMethodIdsInfo[index][0],
      ];
    }
  }
  const categoryArray = [];
  for (const [key, val] of Object.entries(categories)) {
    const obj = {
      name: val[0]?.typeTitle,
      value: val,
      type: val[0]?.type,
    };
    categoryArray.push(obj);
  }

  return categoryArray;
}

export const getPaymentMethodCategoryDict = () => {
  return JSON.parse(localStorage.getItem("paymentMethodCategoryDict"));
};

export const handleClickOutsideCloseDropdown = (
  event,
  dropdownRef,
  functionToCloseDropdown
) => {
  if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
    functionToCloseDropdown(false);
  }
};

export const isMasqueradeUrl = function () {
  return window.location.hostname.includes("masquerade-");
};

export const generateColorShades = (color) => {
  let shadesArray = new Values(color).all(15).map((obj) => {
    return `#${obj?.hex}`;
  });
  shadesArray.reverse();
  return shadesArray.slice(5);
};

export const isTimeLeft = (timeZone, timeStamp) => {
  const currentTime = currentTimeInTimezone(new Date(), timeZone);
  const newTimeStampFormat = parseAsDate(timeStamp)?.$d;
  const expiryTime = currentTimeInTimezone(newTimeStampFormat, timeZone);
  const date1 = new Date(expiryTime);
  const date2 = new Date(currentTime);
  const milliseconds = date1 - date2;
  return milliseconds;
};

function hexToRgba(hexOrRgba, alpha = 1) {
  // Regular expression to check if input is a valid hex color (3 or 6 digits)
  const isValidHex = /^#([A-Fa-f0-9]{3}){1,2}$/;

  // Regular expression to check if input is in rgba format
  const isRgba = /^rgba?\((\d{1,3},\s*){2}\d{1,3}(,\s*\d?(\.\d+)?)?\)$/;

  // If input is already in rgba format, convert to hex
  if (isRgba.test(hexOrRgba)) {
    return rgbaToHex(hexOrRgba);
  }

  // If it's not a valid hex code, throw an error
  if (!isValidHex.test(hexOrRgba)) {
    throw new Error("Invalid hex or rgba color code");
  }

  // Remove the leading hash symbol if it's there
  let hex = hexOrRgba.replace("#", "");

  // Handle 3-digit hex (e.g., #abc becomes #aabbcc)
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // Extract the RGB components
  const r = parseInt(hex.slice(0, 2), 16); // Red
  const g = parseInt(hex.slice(2, 4), 16); // Green
  const b = parseInt(hex.slice(4, 6), 16); // Blue

  // Return the rgba string
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Helper function to convert rgba to hex
export function rgbaToHex(rgba) {
  const result = rgba.match(/\d+/g); // Extract all the numbers from the rgba string

  const r = parseInt(result[0]).toString(16).padStart(2, "0");
  const g = parseInt(result[1]).toString(16).padStart(2, "0");
  const b = parseInt(result[2]).toString(16).padStart(2, "0");
  const a = result[3]
    ? Math.round(parseFloat(result[3]) * 255)
      .toString(16)
      .padStart(2, "0")
    : "";

  return `#${r}${g}${b}${a}`; // Alpha is optional, so it might return #RRGGBB or #RRGGBBAA
}

export const sortArrayValue = (data, key) => {
  if (!data?.length) return [];
  if (key) {
    return data.sort((a, b) =>
      a[key] && b[key]
        ? a[key].replaceAll(" ", "").localeCompare(b[key].replaceAll(" ", ""))
        : true
    );
  }
  return data.sort((a, b) => a.localeCompare(b));
};

export const hexToRGBA = (hex, opacity) => {
  let r = 0,
    g = 0,
    b = 0;

  // Handle shorthand hex like #03F
  if (hex?.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // Handle full hex like #0033FF
  else if (hex?.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const debounce = (callback, delay) => {
  let timeoutId;
  return function (...args) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      callback(...args)
    }, delay)
  }
}
export const getDateRange = (value, data) => {
  if (value === "yesterday") {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const endOfYesterday = new Date();
    endOfYesterday.setDate(endOfYesterday.getDate() - 1);
    endOfYesterday.setHours(23, 59, 59, 999);
    return {
      startDate: yesterday,
      endDate: endOfYesterday
    }
  } else if (value === "today") {
    const today = new Date();
    today.setDate(today.getDate());
    today.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return {
      startDate: today,
      endDate: endOfToday
    }
  } else if (value === "last7Days") {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setDate(todayEnd.getDate() - 1);
    todayEnd.setHours(23, 59, 59, 999);
    return {
      startDate: startDate,
      endDate: todayEnd
    }
  } else if (value === "lastMonth") {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setDate(todayEnd.getDate() - 1);
    todayEnd.setHours(23, 59, 59, 999);
    return {
      startDate: startDate,
      endDate: todayEnd
    }
  } else if (value === "singleDate") {
    if (data?.selectedDate) {
      const startDate = new Date(data?.selectedDate);
      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate());
      const todayEnd = new Date(data?.selectedDate);
      todayEnd.setHours(23, 59, 59, 999);
      todayEnd.setDate(todayEnd.getDate());
      return {
        startDate: startDate,
        endDate: todayEnd
      }
    }
  } else if (value === "customizedDates") {
    if (data?.startDate && data?.endDate) {
      const startDateRange = new Date(data?.startDate);
      startDateRange.setHours(0, 0, 0, 0);
      const todayEnd = new Date(data?.endDate);
      todayEnd.setHours(23, 59, 59, 999);
      todayEnd.setDate(todayEnd.getDate());
      return {
        startDate: startDateRange,
        endDate: todayEnd
      }
    }
  }
};

export const formatDateToUTC = (date) => {
  if (date) {
    let dateIST = new Date(date);
    if (!isNaN(dateIST.getTime())) {
      let dateUTC = dateIST.toISOString();
      return dateUTC;
    }
  }
  return null;
};

export function formatAmount({ amount, locale = "en-IN", currency = "INR", timezone = "IST", showCurrency = false }) {
  if (isNaN(amount) || amount === null) return amount;
  let currencyCode = localStorage.getItem('currencyCode');
  let countryLocale = localStorage.getItem('countryLocale');
  if (!currencyCode && !countryLocale) {
    // const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const merchantSettings = JSON.parse(localStorage.getItem("merchantSettings"));
    const timeZone = Intl.DateTimeFormat(undefined, { timeZone: timezone || merchantSettings?.timezone || 'IST' }).resolvedOptions().timeZone;

    const countryName = timeZoneToCountry[timeZone];
    let countryObj = CurrencyData.find(d => d.countryName.toLowerCase() === countryName.toLowerCase());
    currencyCode = countryObj?.currencyCode;
    countryLocale = countryObj?.locale;
    localStorage.setItem('currencyCode', currencyCode);
    localStorage.setItem('countryLocale', countryLocale);
  }

  return new Intl.NumberFormat(
    locale || countryLocale,
    showCurrency ? { style: "currency", currency: currency || currencyCode } : {})
    .format(amount || 0);
}

export function getLocaleFromTimezone(timezone) {
  const merchantSettings = JSON.parse(localStorage.getItem("merchantSettings"));
  return Intl.DateTimeFormat(undefined, { timeZone: timezone || merchantSettings?.timezone || 'IST' }).resolvedOptions().locale;
}

export const formatDateWithoutSeconds = (timestamp) => {
  console.log("timestamp:", timestamp);

  const originalString = timestamp;
  const modifiedString = originalString.slice(0, 16) + originalString.slice(19);
  return modifiedString;
};

export const PAGE_LIST = [5, 10, 20, 25, 40, 50, 100];
